import { useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'

// Mirrors @wetu/design-system-components DropdownButton, split in two: the label runs
// onClick and only the chevron opens the menu. Items may carry an icon and a description.
const DropdownButton = ({ id, label, onClick, items, align = 'end', className }) => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef(null)
    const toggleRef = useRef(null)
    const itemRefs = useRef([])

    const close = (returnFocus = false) => {
        setIsOpen(false)
        if (returnFocus) toggleRef.current?.focus()
    }

    useEffect(() => {
        if (!isOpen) return

        itemRefs.current[0]?.focus()

        const handlePointerDown = (event) => {
            if (!containerRef.current?.contains(event.target)) setIsOpen(false)
        }

        document.addEventListener('mousedown', handlePointerDown)
        return () => document.removeEventListener('mousedown', handlePointerDown)
    }, [isOpen])

    const handleMenuKeyDown = (event) => {
        const focusable = itemRefs.current.filter(Boolean)
        const index = focusable.indexOf(document.activeElement)

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault()
                focusable[(index + 1) % focusable.length]?.focus()
                break
            case 'ArrowUp':
                event.preventDefault()
                focusable[(index - 1 + focusable.length) % focusable.length]?.focus()
                break
            case 'Escape':
                event.preventDefault()
                close(true)
                break
            case 'Tab':
                close()
                break
            default:
        }
    }

    const handleItemClick = (item) => {
        close()
        item.onClick()
    }

    return (
        <div ref={containerRef} id={id} className={classnames('ds-dropdown-button', { 'is-open': isOpen }, className)}>
            <button type="button" className="ds-dropdown-button__action" onClick={onClick}>
                <span className="ds-dropdown-button__label">{label}</span>
            </button>
            <button
                ref={toggleRef}
                type="button"
                className="ds-dropdown-button__toggle"
                aria-label={`${label} options`}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={() => setIsOpen(!isOpen)}
            >
                <FontAwesomeIcon icon={faChevronDown} className="ds-dropdown-button__chevron" aria-hidden="true" />
            </button>

            {isOpen && (
                <div role="menu" className={classnames('ds-dropdown-menu', `ds-dropdown-menu--${align}`)} onKeyDown={handleMenuKeyDown}>
                    {items.map((item, index) => (
                        <button
                            key={item.id}
                            id={item.elementId}
                            ref={(element) => { itemRefs.current[index] = element }}
                            type="button"
                            role="menuitem"
                            tabIndex={-1}
                            className="ds-dropdown-menu__item"
                            onClick={() => handleItemClick(item)}
                        >
                            {item.icon && <FontAwesomeIcon icon={item.icon} className="ds-dropdown-menu__icon" aria-hidden="true" />}
                            <span className="ds-dropdown-menu__text">
                                <span className="ds-dropdown-menu__label">{item.label}</span>
                                {item.description && <span className="ds-dropdown-menu__description">{item.description}</span>}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

export default DropdownButton
